import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { CheckCircleIcon, CreditCardIcon, ArrowRightIcon, Loader2Icon, XCircleIcon } from 'lucide-react';
import axios from 'axios';

const EmailVerified = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verificationData, setVerificationData] = useState(null);
  const [error, setError] = useState('');
  const [hasVerified, setHasVerified] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Check if this token was already verified in this session
    const verifiedKey = `verified_${token}`;
    const cachedData = sessionStorage.getItem(verifiedKey);
    
    if (cachedData) {
      // Use cached verification data
      try {
        setVerificationData(JSON.parse(cachedData));
        setVerifying(false);
        setHasVerified(true);
        return;
      } catch (e) {
        sessionStorage.removeItem(verifiedKey);
      }
    }
    
    // Prevent duplicate verification attempts
    if (hasVerified) return;
    
    const verifyEmail = async () => {
      if (!token) {
        setError('Geçersiz doğrulama bağlantısı');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/verify-email/${token}`);
        setVerificationData(response.data);
        setHasVerified(true);
        // Cache successful verification
        sessionStorage.setItem(verifiedKey, JSON.stringify(response.data));
      } catch (err) {
        // Check if error is because token was already used (which means it worked)
        const errorDetail = err.response?.data?.detail || '';
        if (errorDetail.includes('kullanılmış') || errorDetail.includes('used')) {
          // Token was used, which means verification succeeded before
          // Show error but user is actually verified
          setError('Bu doğrulama bağlantısı zaten kullanılmış. Hesabınız doğrulanmıştır.');
        } else {
          setError(errorDetail || 'E-posta doğrulama başarısız oldu');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token, BACKEND_URL, hasVerified]);

  const handleContinueToPayment = () => {
    // TODO: Stripe ödeme sayfasına yönlendir
    navigate('/payment', { state: { email: verificationData?.user_email } });
  };

  const handleGoToDashboard = () => {
    navigate('/login');
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center">
          <Loader2Icon className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">E-posta doğrulanıyor...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircleIcon className="w-12 h-12 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Doğrulama Başarısız</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/register-company')} className="w-full" variant="outline">
              Kayıt Sayfasına Dön
            </Button>
            <Button onClick={() => navigate('/login')} className="w-full">
              Giriş Yap
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-8 md:p-12 shadow-2xl">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center animate-bounce">
              <CheckCircleIcon className="w-16 h-16 text-white" />
            </div>
            <div className="absolute inset-0 w-24 h-24 bg-green-400 rounded-full animate-ping opacity-20"></div>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-4 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
          ✅ E-posta Adresiniz Doğrulandı!
        </h1>

        {/* Success Message */}
        <div className="space-y-4 text-center mb-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Tebrikler! E-posta adresiniz <strong className="text-green-600">{verificationData?.user_email}</strong> başarıyla doğrulandı. 🎉
          </p>

          {verificationData?.needs_payment ? (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-6 rounded-lg my-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center justify-center">
                  <CreditCardIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Son Bir Adım Kaldı!
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Hesabınızı aktifleştirmek ve <strong>AdvanceCash.io</strong> platformunu kullanmaya başlamak için 
                  abonelik ödemenizi tamamlamanız gerekmektedir.
                </p>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600">
                    <strong className="text-blue-700">Seçtiğiniz Paket:</strong> {verificationData?.subscription_plan === 'starter' ? 'Starter' : 
                    verificationData?.subscription_plan === 'professional' ? 'Professional' : 'Enterprise'}
                  </p>
                </div>
              </div>

              {/* Payment Button */}
              <Button
                onClick={handleContinueToPayment}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg font-semibold shadow-lg"
              >
                <CreditCardIcon className="w-5 h-5 mr-2" />
                Ödeme Sayfasına Geç
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-sm text-gray-500 mt-4">
                🔒 Güvenli ödeme: Stripe altyapısı ile korunmaktasınız
              </p>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg my-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  🎁 Ücretsiz Deneme Aktif!
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>14 günlük</strong> ücretsiz deneme süreniz başladı! 
                  Artık tüm özelliklere erişebilir ve platformu keşfedebilirsiniz.
                </p>
              </div>

              {/* Dashboard Button */}
              <Button
                onClick={handleGoToDashboard}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 text-lg font-semibold shadow-lg"
              >
                Dashboard'a Git
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Button>
            </>
          )}
        </div>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm text-center">
            <div className="text-3xl mb-2">⚡</div>
            <p className="text-sm font-semibold text-gray-700">Hızlı Kurulum</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm text-center">
            <div className="text-3xl mb-2">🔒</div>
            <p className="text-sm font-semibold text-gray-700">Güvenli Sistem</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm text-center">
            <div className="text-3xl mb-2">📊</div>
            <p className="text-sm font-semibold text-gray-700">Detaylı Raporlar</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Sorularınız için: 
            <a href="mailto:destek@advancecash.io" className="text-blue-600 hover:underline ml-1">
              destek@advancecash.io
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default EmailVerified;
